/**
 * Motor Vital — editorial story page (scroll-triggered motion).
 *
 * Port of the WooCommerce story.js without GSAP: IntersectionObserver
 * drives chapter reveals, the Web Animations API handles the motion,
 * a passive scroll handler updates progress bar, rail state and parallax.
 */
( function () {
	'use strict';

	var EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';
	var EASE_IN_OUT = 'cubic-bezier(0.65, 0, 0.35, 1)';

	function prefersReducedMotion() {
		return window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
	}

	function scrollOffset() {
		return parseInt( getComputedStyle( document.documentElement ).getPropertyValue( '--mv-story-scroll-offset' ), 10 ) || 72;
	}

	function scrollToChapter( chapter ) {
		if ( ! chapter ) {
			return;
		}
		var y = chapter.getBoundingClientRect().top + window.scrollY - scrollOffset();
		window.scrollTo( { top: Math.max( 0, y ), behavior: prefersReducedMotion() ? 'auto' : 'smooth' } );
	}

	function formatCount( value, suffix ) {
		var n = Math.round( value );
		return ( n >= 1000 ? n.toLocaleString( 'de-DE' ) : String( n ) ) + suffix;
	}

	function show( el ) {
		el.style.visibility = 'visible';
	}

	function animateIn( el, from, duration, delay ) {
		show( el );
		var keyframes = [ Object.assign( { opacity: 0 }, from ), { opacity: 1, transform: 'none' } ];
		if ( from.clipPath ) {
			keyframes = [ from, { clipPath: 'inset(0 0% 0 0)' } ];
		}
		return el.animate( keyframes, { duration: duration, delay: delay || 0, easing: from.clipPath ? EASE_IN_OUT : EASE_OUT, fill: 'both' } );
	}

	function animateCounter( el ) {
		var target = parseFloat( el.getAttribute( 'data-mv-count' ) ) || 0;
		var suffix = el.getAttribute( 'data-mv-suffix' ) || '';
		var start = null;
		var duration = 2200;

		function frame( now ) {
			if ( start === null ) {
				start = now;
			}
			var t = Math.min( 1, ( now - start ) / duration );
			var eased = 1 - Math.pow( 1 - t, 3 );
			el.textContent = formatCount( target * eased, suffix );
			if ( t < 1 ) {
				window.requestAnimationFrame( frame );
			}
		}
		window.requestAnimationFrame( frame );
	}

	function revealChapter( chapter ) {
		var variant = chapter.getAttribute( 'data-variant' ) || 'reveal';
		var reveals = chapter.querySelectorAll( '[data-mv-reveal]' );
		var listItems = chapter.querySelectorAll( '[data-mv-reveal-item]' );
		var media = chapter.querySelector( '[data-mv-parallax]' );
		var counter = chapter.querySelector( '[data-mv-count]' );

		reveals.forEach( function ( el, i ) {
			animateIn( el, { transform: 'translateY(' + ( variant === 'split' ? 20 : 36 ) + 'px)' }, 900, i * 90 );
		} );

		listItems.forEach( function ( el, i ) {
			animateIn( el, { transform: 'translateX(-16px)' }, 650, 250 + i * 70 );
		} );

		if ( media ) {
			if ( variant === 'wipe' ) {
				animateIn( media, { clipPath: 'inset(0 100% 0 0)' }, 1100, 0 );
			} else if ( variant === 'split' ) {
				var fromX = chapter.classList.contains( 'mv-story__chapter--media-left' ) ? -48 : 48;
				animateIn( media, { transform: 'translateX(' + fromX + 'px)' }, 1050, 0 );
			} else if ( variant === 'hero' ) {
				var img = media.querySelector( '.mv-story__img' ) || media;
				img.animate( [ { transform: 'scale(1.12)' }, { transform: 'scale(1)' } ], { duration: 1400, easing: EASE_OUT, fill: 'both' } );
			}
		}

		if ( counter ) {
			animateCounter( counter );
		}
	}

	function setupStory( root ) {
		var chapters = Array.prototype.slice.call( root.querySelectorAll( '.mv-story__chapter' ) );
		var bar = root.querySelector( '.mv-story__progress-bar' );
		var progressWrap = root.querySelector( '.mv-story__progress' );
		var railItems = root.querySelectorAll( '[data-mv-rail]' );
		var counterEl = root.querySelector( '[data-mv-current]' );
		var total = chapters.length;

		if ( ! total ) {
			return;
		}

		chapters.forEach( function ( chapter, index ) {
			chapter.id = chapter.id || 'mv-story-chapter-' + ( index + 1 );
		} );

		railItems.forEach( function ( item, index ) {
			var btn = item.querySelector( '.mv-story__rail-btn' );
			if ( btn ) {
				btn.addEventListener( 'click', function () {
					scrollToChapter( chapters[ index ] );
				} );
			}
		} );

		if ( prefersReducedMotion() ) {
			root.querySelectorAll( '[data-mv-count]' ).forEach( function ( el ) {
				el.textContent = formatCount( parseFloat( el.getAttribute( 'data-mv-count' ) ) || 0, el.getAttribute( 'data-mv-suffix' ) || '' );
			} );
			return;
		}

		root.classList.add( 'is-enhanced' );

		/* "Als Nächstes" links between chapters */
		chapters.forEach( function ( chapter, index ) {
			if ( index >= total - 1 ) {
				return;
			}
			var next = chapters[ index + 1 ];
			var nextTitle = next.getAttribute( 'data-mv-chapter-title' ) || '';
			var grid = chapter.querySelector( '.mv-story__grid' );
			if ( ! nextTitle || ! grid ) {
				return;
			}
			var link = document.createElement( 'a' );
			link.className = 'mv-story__chapter-next';
			link.href = '#' + next.id;
			link.setAttribute( 'aria-label', nextTitle );
			link.innerHTML =
				'<span class="mv-story__chapter-next-kicker">Als Nächstes</span>' +
				'<span class="mv-story__chapter-next-body">' +
					'<span class="mv-story__chapter-next-title"></span>' +
					'<span class="mv-story__chapter-next-arrow" aria-hidden="true">↓</span>' +
				'</span>';
			link.querySelector( '.mv-story__chapter-next-title' ).textContent = nextTitle;
			link.addEventListener( 'click', function ( event ) {
				event.preventDefault();
				scrollToChapter( next );
			} );
			grid.appendChild( link );
		} );

		/* Reveal on enter (once) */
		var revealed = new WeakSet();
		var observer = new IntersectionObserver( function ( entries ) {
			entries.forEach( function ( entry ) {
				if ( entry.isIntersecting && ! revealed.has( entry.target ) ) {
					revealed.add( entry.target );
					revealChapter( entry.target );
					observer.unobserve( entry.target );
				}
			} );
		}, { rootMargin: '0px 0px -22% 0px', threshold: 0.05 } );
		chapters.forEach( function ( chapter ) {
			observer.observe( chapter );
		} );

		/* Scroll-driven state: progress, rail, active chapter, parallax */
		var firstChapter = chapters[ 0 ];
		var lastIdx = -1;
		var ticking = false;
		var parallaxMedia = chapters.filter( function ( ch ) {
			return ( ch.getAttribute( 'data-variant' ) || 'reveal' ) === 'reveal' || ch.getAttribute( 'data-variant' ) === 'counter';
		} ).map( function ( ch ) {
			return { chapter: ch, media: ch.querySelector( '[data-mv-parallax]' ) };
		} ).filter( function ( pair ) {
			return pair.media;
		} );

		function setActive( idx ) {
			if ( idx === lastIdx ) {
				return;
			}
			lastIdx = idx;
			if ( counterEl ) {
				counterEl.textContent = ( '0' + ( idx + 1 ) ).slice( -2 );
			}
			railItems.forEach( function ( item, r ) {
				item.classList.toggle( 'is-active', r === idx );
				item.classList.toggle( 'is-done', r < idx );
				var btn = item.querySelector( '.mv-story__rail-btn' );
				if ( btn ) {
					if ( r === idx ) {
						btn.setAttribute( 'aria-current', 'true' );
					} else {
						btn.removeAttribute( 'aria-current' );
					}
				}
			} );
			if ( railItems[ idx ] ) {
				railItems[ idx ].scrollIntoView( { behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'center' } );
			}
			chapters.forEach( function ( ch, i ) {
				ch.classList.toggle( 'is-current', i === idx );
			} );
		}

		function update() {
			ticking = false;
			var doc = document.documentElement;
			var max = Math.max( 1, doc.scrollHeight - window.innerHeight );
			var progress = Math.max( 0, Math.min( 1, window.scrollY / max ) );
			if ( bar ) {
				bar.style.transform = 'scaleX(' + progress + ')';
			}
			if ( progressWrap ) {
				progressWrap.setAttribute( 'aria-valuenow', String( Math.round( progress * 100 ) ) );
			}

			var offset = scrollOffset();
			var firstRect = firstChapter.getBoundingClientRect();
			var storyRect = root.getBoundingClientRect();
			root.classList.toggle( 'is-rail-visible', firstRect.top <= offset && storyRect.bottom > offset );

			var marker = window.innerHeight * 0.38;
			var active = 0;
			for ( var i = 0; i < chapters.length; i++ ) {
				if ( chapters[ i ].getBoundingClientRect().top <= marker ) {
					active = i;
				}
			}
			setActive( active );

			parallaxMedia.forEach( function ( pair ) {
				var rect = pair.chapter.getBoundingClientRect();
				var span = window.innerHeight + rect.height;
				var t = Math.max( 0, Math.min( 1, ( window.innerHeight - rect.top ) / span ) );
				pair.media.style.transform = 'translateY(' + ( -28 * t ).toFixed( 1 ) + 'px)';
			} );
		}

		function onScroll() {
			if ( ! ticking ) {
				ticking = true;
				window.requestAnimationFrame( update );
			}
		}

		window.addEventListener( 'scroll', onScroll, { passive: true } );
		window.addEventListener( 'resize', onScroll, { passive: true } );
		update();
	}

	function setupLeadIntro() {
		var lead = document.querySelector( '[data-mv-story-lead]' );
		if ( ! lead || prefersReducedMotion() ) {
			return;
		}
		lead.querySelectorAll( '[data-mv-lead-reveal]' ).forEach( function ( el, i ) {
			el.animate( [ { opacity: 0, transform: 'translateY(32px)' }, { opacity: 1, transform: 'none' } ], { duration: 950, delay: 120 + i * 90, easing: EASE_OUT, fill: 'both' } );
		} );
		lead.querySelectorAll( '.mv-stat-grid__value' ).forEach( function ( el, i ) {
			el.animate( [ { opacity: 0, transform: 'translateY(12px) scale(0.94)' }, { opacity: 1, transform: 'none' } ], { duration: 650, delay: 450 + i * 80, easing: EASE_OUT, fill: 'both' } );
		} );
		var visual = lead.querySelector( '.mv-story-lead__visual' );
		if ( visual ) {
			visual.animate( [ { opacity: 0, transform: 'translateX(24px) scale(0.97)' }, { opacity: 1, transform: 'none' } ], { duration: 1150, delay: 280, easing: EASE_OUT, fill: 'both' } );
		}
	}

	function init() {
		document.querySelectorAll( '[data-mv-story]' ).forEach( setupStory );
		setupLeadIntro();
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();
